import { Injectable, signal, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Data, Router } from '@angular/router';
import { AuthService } from '@core/auth.service';
import { LoadingService } from '@core/loading.service';
import { ModalController } from '@ionic/angular';
import { Observable, Subject, throwError } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { CommonVariable } from 'src/app/app.constants';
import { RegProfileDialogComponentComponent } from 'src/app/components/reg-profile-dialog-component/reg-profile-dialog-component.component';
import { CreditCheckoutComponent } from 'src/app/pages/store/credit-checkout/credit-checkout.component';
import { StorePaymentComponent } from 'src/app/pages/store/store-payment/store-payment.component';
import { StoreRepositoryService } from 'src/app/pages/store/store-repository.service';
import { SyndicateTicketsComponent } from 'src/app/pages/syndicate/syndicate-tickets/syndicate-tickets.component';
import { PaymentDataService } from '../payment-data.service';
import { PaymentService } from '../payment.service';
import { VoucherService } from '../vouchers.service';

@Injectable({
  providedIn: 'root'
})
export class AppPaymentService implements OnDestroy {
  userAuthDetails$ = this.authService.userDetails$;
  offers: Data;
  sqlAPICallResponse: string;
  public checkOutDetails: string;
  public preferredCurrency: string;
  public languageCode: string;
  private unsubscribe$ = new Subject<void>();
  jackpotValueSignal = signal<string>('');
  public isVoucherClaimed = signal<boolean>(false);
  /** Prevents re-entrant initiatePayment for the same offer deep-link. */
  private inFlightOfferKey: string | null = null;
  private completedOfferKey: string | null = null;

  constructor(
    private authService: AuthService,
    private paymentService: PaymentService,
    private modalController: ModalController,
    private loadingService: LoadingService,
    private voucherService: VoucherService,
    private router: Router,
    private matDailog: MatDialog,
    private paymntDataService: PaymentDataService,
    private storeRespository: StoreRepositoryService
  ) {
    this.preferredCurrency = CommonVariable.preferredCurrency;
    this.languageCode = CommonVariable?.preferredLanguage;

    this.userAuthDetails$.pipe(
      takeUntil(this.unsubscribe$)
    ).subscribe((user: Data) => {
      CommonVariable.preferredLanguage = user?.language_code ?? localStorage.getItem("language_code") ?? CommonVariable?.defaultLanguage;
      this.languageCode = CommonVariable?.preferredLanguage;
      // translate.use(this.languageCode ? this.languageCode : CommonVariable?.preferredLanguage);
    })
  }

  /** Clear deep-link guards when payment= is removed from the URL (e.g. checkout dismiss). */
  clearPaymentDeepLinkState(): void {
    this.inFlightOfferKey = null;
    this.completedOfferKey = null;
  }

  private offerDeepLinkKey(params: Data): string | null {
    if (params?.type === 'award' && params?.payment != null && params?.payment !== '') {
      return `award:${params.payment}`;
    }
    const id = params?.payment ?? params?.hofferid;
    if (id == null || id === '') return null;
    return `${params?.payment ? 'payment' : 'hofferid'}:${id}`;
  }

  initiatePayment(params: Data) {
    try {
      const offerKey = this.offerDeepLinkKey(params);
      // Ignore empty queryParams emissions (store page fires on every navigation).
      if (!offerKey) return;
      if (this.inFlightOfferKey === offerKey || this.completedOfferKey === offerKey) return;

      if (params?.type === "award") {
        this.inFlightOfferKey = offerKey;
        const paymentId = Number(params?.payment);
        this.paymntDataService
          .getCreditTopupOfferDetail()
          .pipe(
            take(1),
            takeUntil(this.unsubscribe$)
          )
          .subscribe({
            next: (offer: Data) => {
              let matched = false;
              offer?.options?.forEach((eachData: Data) => {
                if (
                  eachData?.award_id?.CAD === paymentId ||
                  eachData?.award_id?.EUR === paymentId ||
                  eachData?.award_id?.GBP === paymentId ||
                  eachData?.award_id?.USD === paymentId
                ) {
                  matched = true;
                  this.getCheckoutDetails(eachData);
                  setTimeout(() => {
                    this.completedOfferKey = offerKey;
                    this.inFlightOfferKey = null;
                    this.routeToStorePayment(eachData);
                  }, 2000);
                }
              });
              if (!matched) {
                this.inFlightOfferKey = null;
                this.loadingService.dismiss();
                this.authService.errorHandlerLogs('[initiatePayment] Award offer not found', { paymentId });
              }
            },
            error: (err) => {
              this.inFlightOfferKey = null;
              this.loadingService.dismiss();
              this.authService.errorHandlerLogs('[initiatePayment] Award offer lookup failed', err);
            }
          });
      } else {
        this.inFlightOfferKey = offerKey;
        const idType = params?.payment ? "payment" : "hofferid";
        const offerDetail$ = this.authService.getOfferDetail(
          params?.payment || params?.hofferid,
          idType
        );
        if (!offerDetail$) {
          this.inFlightOfferKey = null;
          this.loadingService.dismiss();
          this.authService.errorHandlerLogs('[initiatePayment] getOfferDetail returned empty', {
            idType,
            id: params?.payment || params?.hofferid,
          });
          return;
        }
        (offerDetail$ as Observable<Data>)
          .pipe(
            take(1),
            takeUntil(this.unsubscribe$)
          )
          .subscribe({
            next: async (offer: Data) => {
              this.offers = offer;
              if (!offer || (typeof offer === 'object' && !Object.keys(offer).length)) {
                this.inFlightOfferKey = null;
                this.loadingService.dismiss();
                this.authService.errorHandlerLogs(
                  '[initiatePayment] Offer not found; checkout aborted',
                  { idType, id: params?.payment || params?.hofferid }
                );
                return;
              }
              if (this.offers?.payment_type === "credit") {
                this.paymentService.salesParams = params;
                await this.modalController
                  .create({
                    component: CreditCheckoutComponent,
                    componentProps: {
                      data: this.offers,
                    },
                    cssClass: "modal-fullScreen",
                  })
                  .then(async (modal) => {
                    this.completedOfferKey = offerKey;
                    this.inFlightOfferKey = null;
                    this.loadingService.dismiss();
                    modal.onDidDismiss().then(() => {
                      this.clearPaymentDeepLinkState();
                      return;
                    });
                    return await modal.present();
                  })
                  .catch((error) => {
                    this.inFlightOfferKey = null;
                    this.loadingService.dismiss();
                    return error;
                  });
              } else {
                let data = Object.assign({
                  line: offer?.lines - 1,
                  real_name: offer?.lottery,
                  prize: offer?.price,
                  page: "store",
                  productid: offer?.productid ? [...offer?.productid] : [],
                  selectedDay: Array.isArray(offer?.draw_day) ? [...offer.draw_day.map((m) => m?.trim())] : [],
                  checkout: true,
                  offers: offer,
                  selectedDiscount: offer?.discount,
                  selectedLine: offer?.lines - 1,
                });

                this.storeRespository.preloadTopLotteries(Array.isArray(data) ? data : [data]);
                const storedData = sessionStorage.getItem("recentLotteryDetails");
                if (storedData) {
                  const parsed = JSON.parse(storedData);
                  this.jackpotValueSignal.set(parsed?.jackpot_value || '');
                }
                const lotteryDetails = {
                  page: data?.page,
                  jackpot_value:this.jackpotValueSignal(),
                };
                sessionStorage.setItem("recentLotteryDetails", JSON.stringify(lotteryDetails));

                let queryParams = params;
                //  delete queryParams.payment;

                data = { ...data, ...queryParams };
                // Setting this value to decide the availability of discount in payment page as per voucher claim status
                data.discount_available = this.isVoucherClaimed();
                this.completedOfferKey = offerKey;
                this.inFlightOfferKey = null;
                this.storePaymentPage(data);
              }
            },
            error: (err) => {
              this.inFlightOfferKey = null;
              this.loadingService.dismiss();
              this.authService.errorHandlerLogs('[initiatePayment] getOfferDetail failed', err);
              return err;
            }
          });
      }
    } catch (e) {
      this.inFlightOfferKey = null;
      this.loadingService.dismiss();
      throwError(e);
    }
  }
  private async getCheckoutDetails(offer) {
    if (offer?.content?.checkout_details) {
      this.authService.currencyNodeDvData = offer;
      this.authService
        .getDVFromString(offer?.content?.checkout_details)
        .then((dvString: string) => {
          if (dvString) {
            this.checkOutDetails = dvString;
          } else {
            return "";
          }
        })
        .catch((err) => {
          return err;
        });
    }
  }

  private async routeToStorePayment(topUpData) {
    const storePayment = await this.modalController.create({
      component: StorePaymentComponent,
      cssClass: "modal-fullScreen",
      componentProps: {
        data: this.formPaymentDataForTopUp(topUpData),
      },
    });
    this.loadingService.dismiss();
    return await storePayment.present();
  }

  private formPaymentDataForTopUp(offer: Data) {
    const offerDetails = {
      prize: offer?.int_price?.[this.preferredCurrency] ? offer?.int_price?.[this.preferredCurrency] : offer?.price,
      offers: offer,
      checkout: true,
      draw: "",
      game: this.checkOutDetails,
      line: "",
      offer_id: offer?.award_id[this.preferredCurrency],
      real_name:
        offer?.checkout_details && offer?._checkout_details && offer?._checkout_details[this.languageCode]
          ? offer?._checkout_details[this.languageCode]
          : this.checkOutDetails,
      isTopUp: true,
      page: "topUp",
    };
    return offerDetails;
  }

  private storePaymentPage(data: Data) {
    localStorage.setItem("payType", "cash");
    this.modalController
      .create({
        component: StorePaymentComponent,
        cssClass: "modal-fullScreen",
        componentProps: {
          data: data,
        },
      })
      .then((paymentModal) => {
        this.loadingService.dismiss();
        paymentModal.present();
        paymentModal.onDidDismiss().then((res) => {
          this.clearPaymentDeepLinkState();
          if (res?.data && res?.data?.p_type == "10") {
            this.sqlAPICallResponse = res?.data?.syndicateId;
            if (!this.sqlAPICallResponse) {
              this.voucherService.setSyndicatePending();
              // To decide the next flow when modal is dismissed based on different scenarioes
              let payType = "";
              if (localStorage.getItem("payType")) {
                payType = localStorage.getItem("payType");
                localStorage.removeItem("payType");
              }
              if (payType === "cash") {
                this.router.navigateByUrl("mainTabs/tabs/syndicate");
              } else if (payType === "cash-issue") {
                this.loadingService.dismiss();
                this.router.navigateByUrl("mainTabs/tabs/store");
              } else {
                this.matDailog.open(RegProfileDialogComponentComponent, {
                  panelClass: "ongoingWarning-size",
                  backdropClass: "ongoingWarning-back-drop-color",
                  disableClose: true,
                });
              }
              return;
            }
          }
          if (res?.data && res?.data?.syndicateId) {
            this.paymentService
              .getSyndicateInfoWithProducts(res?.data?.syndicateId)
              .pipe(
                map((detail: Data) => {
                  if (!detail?.[0]?.data()) return;
                  const syndicateInfo = detail?.[0].data();
                  syndicateInfo.product = [];
                  const productDocs = detail?.[1].docs;
                  for (let index = 0; index < productDocs?.length; index++) {
                    syndicateInfo.product.push(productDocs?.[index]?.data());
                  }
                  if (!syndicateInfo) return;
                  syndicateInfo["cardType"] = "Managed";
                  return syndicateInfo;
                }),
                takeUntil(this.unsubscribe$)
              )
              ?.subscribe(
                (syndicateInfo) => {
                  this.showTicket(syndicateInfo, paymentModal);
                },
                (err) => {
                  return err;
                }
              );
          }
        });
      })
      .catch((err) => {
        return err;
      });
  }

  private async showTicket(syndicateInfo, paymentModal) {
    const syndicateTickets = await this.modalController.create({
      component: SyndicateTicketsComponent,
      componentProps: { syndicate: syndicateInfo },
      cssClass: "modal-fullScreen",
    });

    syndicateTickets
      .onDidDismiss()
      .then(() => {
        paymentModal.dismiss();
      })
      .catch((err) => {
        return err;
      });
    return await syndicateTickets.present();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
